import { socketIOSettings } from '@config/socket.io';
import { API_ENDPOINT } from '@constants/env';
import { UPLOAD_DOCUMENTS_EVENTS } from '@conversed/shared/src/central/constants/socket';
import type {
  StartUploadPayload,
  UploadDocumentCompletedData,
  UploadDocumentErrorData,
  UploadDocumentProgressData,
} from '@conversed/shared/src/central/dto/upload-documents.dto';
import {
  reconnectAfterRetryHint,
  reconnectAfterServerDisconnect,
} from '@helpers/socketHelpers';
import { useMainSelector } from '@hooks/useMainSelector';
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

const { PLATFORM_TO_BACKEND, BACKEND_TO_PLATFORM } = UPLOAD_DOCUMENTS_EVENTS;

export const useUploadDocumentsSocket = (
  onProgress?: (data: UploadDocumentProgressData) => void,
  onCompleted?: (data: UploadDocumentCompletedData) => void,
  onError?: (data: UploadDocumentErrorData) => void,
) => {
  const {
    login: { accessToken },
  } = useMainSelector();

  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Store callbacks in refs to avoid reconnecting when they change
  const onProgressRef = useRef(onProgress);
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    // Only create socket if it doesn't exist or is disconnected
    if (socketRef.current?.connected) {
      return;
    }

    console.log('Creating new socket connection...');

    socketRef.current = io(API_ENDPOINT, {
      ...socketIOSettings,
      path: '/sockets/platform',
      auth: {
        token: accessToken,
      },
    });
    reconnectAfterServerDisconnect(socketRef.current);
    const cancelRetryReconnect = reconnectAfterRetryHint(socketRef.current);

    socketRef.current.on('connect', () => {
      setIsConnected(true);
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
    });

    socketRef.current.on(
      'connect_error',
      (error: Error & { data?: { 'retry-ms'?: number; reason?: string } }) => {
        const { data } = error;
        const { 'retry-ms': retryMs } = data || {};
        setIsConnected(false);

        if (typeof retryMs === 'number') {
          return console.error(
            `Websocket connection blocked, retrying in ${retryMs} ms`,
          );
        }

        if (data?.reason) {
          return console.error(
            `Websocket connection blocked, reason ${data?.reason}`,
          );
        }

        return console.error('Unknown websocket error, connection blocked');
      },
    );

    // Use refs for event handlers to avoid reconnecting
    socketRef.current.on(BACKEND_TO_PLATFORM.PROGRESS, (data) => {
      onProgressRef.current?.(data);
    });

    socketRef.current.on(BACKEND_TO_PLATFORM.COMPLETED, (data) => {
      onCompletedRef.current?.(data);
    });

    socketRef.current.on(BACKEND_TO_PLATFORM.ERROR, (data) => {
      onErrorRef.current?.(data);
    });

    return () => {
      cancelRetryReconnect();

      console.log('Cleaning up socket connection...');
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
    };
  }, [accessToken]);

  const startUpload = useCallback((payload: StartUploadPayload) => {
    if (!socketRef.current?.connected) {
      console.error('Socket not connected');
      return false;
    }

    socketRef.current.emit(PLATFORM_TO_BACKEND.START_UPLOAD, payload);
    return true;
  }, []);

  return {
    socket: socketRef.current,
    startUpload,
    isConnected,
  };
};
